import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

// Styled components
const ConsentContainer = styled.div`
  position: fixed;
  bottom: 20px;
  left: 20px;
  right: 20px;
  max-width: 500px;
  margin: 0 auto;
  padding: 20px;
  background-color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  z-index: 9999;
  animation: slideUp 0.5s ease;
  
  @keyframes slideUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ConsentTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 1.2rem;
  color: #333;
`;

const ConsentText = styled.p`
  font-size: 0.9rem;
  line-height: 1.5;
  color: #555;
  margin-bottom: 15px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 8px 15px;
  font-size: 0.9rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:focus {
    outline: none;
  }
`;

const AcceptButton = styled(Button)`
  background-color: #428DE7;
  color: white;
  
  &:hover {
    background-color: #3579d3;
  }
`;

const DeclineButton = styled(Button)`
  background-color: transparent;
  color: #666;
  border: 1px solid #ccc;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const SettingsButton = styled(Button)`
  background-color: transparent;
  color: #428DE7;
  text-decoration: underline;
  padding: 8px 5px;
  
  &:hover {
    background-color: rgba(66, 141, 231, 0.05);
  }
`;

const SettingsContainer = styled.div`
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #eee;
`;

const ConsentOption = styled.div`
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const OptionLabel = styled.label`
  display: flex;
  flex-direction: column;
  font-size: 0.9rem;
  color: #444;
  cursor: pointer;
  user-select: none;
  flex: 1;
  padding-right: 15px;
  
  &:hover {
    color: #428DE7;
  }
`;

const OptionDescription = styled.span`
  font-size: 0.8rem;
  color: #777;
  display: block;
  margin-top: 2px;
`;

// Switch component
const Switch = styled.div`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  flex-shrink: 0;
  cursor: pointer;
  user-select: none;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:active span:before {
    width: 18px;
  }
`;

const SwitchInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  
  &:checked + span {
    background-color: #428DE7;
  }
  
  &:checked + span:before {
    transform: translateX(26px);
  }
  
  &:disabled + span {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: all 0.3s ease;
  border-radius: 34px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) inset;
  
  &:before {
    position: absolute;
    content: "";
    height: 16px;
    width: 16px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: all 0.3s ease;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
  
  // Thêm hiệu ứng nhấn
  ${Switch}:active & {
    background-color: #b3b3b3;
  }
  
  ${Switch}:active &:before {
    width: 20px;
  }
  
  ${SwitchInput}:checked + &:before {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }
`;

// Thêm hiệu ứng phản hồi khi thay đổi
const FeedbackMessage = styled.div`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #428DE7;
  color: white;
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 0.9rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 10000;
  animation: fadeIn 0.3s ease, fadeOut 0.3s ease 2.7s forwards;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translate(-50%, -10px);
    }
    to {
      opacity: 1;
      transform: translate(-50%, 0);
    }
  }
  
  @keyframes fadeOut {
    from {
      opacity: 1;
      transform: translate(-50%, 0);
    }
    to {
      opacity: 0;
      transform: translate(-50%, -10px);
    }
  }
`;

const CookieConsent = ({ onConsentChange }) => {
  const { t } = useTranslation();
  const [showConsent, setShowConsent] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState({
    necessary: true, // Always true and disabled
    analytics: false,
    marketing: false,
  });
  // Thêm state để hiển thị thông báo phản hồi
  const [feedback, setFeedback] = useState('');

  // Check if consent was already given
  useEffect(() => {
    const storedConsent = localStorage.getItem('cookieConsent');
    
    if (!storedConsent) {
      setShowConsent(true);
    } else {
      try {
        const parsedConsent = JSON.parse(storedConsent);
        setConsent(parsedConsent);
        // Notify parent component about the consent status
        if (onConsentChange) {
          onConsentChange(parsedConsent);
        }
        
        // Also notify Google Consent Mode with full parameters
        window.gtag?.('consent', 'update', {
          analytics_storage: parsedConsent.analytics ? 'granted' : 'denied',
          ad_storage: parsedConsent.marketing ? 'granted' : 'denied',
          ad_user_data: parsedConsent.marketing ? 'granted' : 'denied',
          ad_personalization: parsedConsent.marketing ? 'granted' : 'denied',
          personalization_storage: parsedConsent.marketing ? 'granted' : 'denied',
          functionality_storage: parsedConsent.necessary ? 'granted' : 'denied'
        });
      } catch (e) {
        console.error('Error parsing stored consent:', e);
        setShowConsent(true);
      }
    }
  }, [onConsentChange]);

  const handleAcceptAll = () => {
    const newConsent = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    
    setConsent(newConsent);
    saveConsent(newConsent);
    setShowConsent(false);
  };

  const handleDeclineAll = () => {
    const newConsent = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    
    setConsent(newConsent);
    saveConsent(newConsent);
    setShowConsent(false);
  };

  const handleSaveSettings = () => {
    saveConsent(consent);
    setShowConsent(false);
    setShowSettings(false);
  };

  const saveConsent = (consentData) => {
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    
    // Notify parent component
    if (onConsentChange) {
      onConsentChange(consentData);
    }
    
    // Update Google Consent Mode settings
    window.gtag?.('consent', 'update', {
      analytics_storage: consentData.analytics ? 'granted' : 'denied',
      ad_storage: consentData.marketing ? 'granted' : 'denied',
      ad_user_data: consentData.marketing ? 'granted' : 'denied',
      ad_personalization: consentData.marketing ? 'granted' : 'denied',
      personalization_storage: consentData.marketing ? 'granted' : 'denied',
      functionality_storage: consentData.necessary ? 'granted' : 'denied'
    });
  };

  const handleOptionChange = (option) => {
    const newValue = !consent[option];
    
    setConsent(prev => ({
      ...prev,
      [option]: newValue
    }));
    
    // Hiển thị thông báo phản hồi
    setFeedback(newValue 
      ? t('cookieConsent.enabledMessage', '{{option}} cookies enabled', {option: t(`cookieConsent.${option}`)})
      : t('cookieConsent.disabledMessage', '{{option}} cookies disabled', {option: t(`cookieConsent.${option}`)})
    );
    
    // Tự động ẩn thông báo sau 3 giây
    setTimeout(() => {
      setFeedback('');
    }, 3000);
  };

  if (!showConsent) return null;

  return (
    <>
      {feedback && <FeedbackMessage>{feedback}</FeedbackMessage>}
      
      <ConsentContainer>
        <ConsentTitle>{t('cookieConsent.title', 'Cookie Consent')}</ConsentTitle>
        <ConsentText>
          {t('cookieConsent.description', 'We use cookies to enhance your browsing experience, serve personalized ads or content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.')}
        </ConsentText>
        
        {showSettings ? (
          <>
            <SettingsContainer>
              <ConsentOption>
                <OptionLabel htmlFor="necessary-cookies">
                  {t('cookieConsent.necessary', 'Necessary Cookies')}
                  <OptionDescription>
                    {t('cookieConsent.necessaryDesc', 'Essential for the website to function properly. Cannot be disabled.')}
                  </OptionDescription>
                </OptionLabel>
                <Switch>
                  <SwitchInput 
                    id="necessary-cookies"
                    type="checkbox" 
                    checked={consent.necessary} 
                    disabled 
                  />
                  <Slider />
                </Switch>
              </ConsentOption>
              
              <ConsentOption>
                <OptionLabel htmlFor="analytics-cookies" onClick={() => handleOptionChange('analytics')}>
                  {t('cookieConsent.analytics', 'Analytics Cookies')}
                  <OptionDescription>
                    {t('cookieConsent.analyticsDesc', 'Help us understand how visitors interact with our website.')}
                  </OptionDescription>
                </OptionLabel>
                <Switch onClick={() => handleOptionChange('analytics')}>
                  <SwitchInput 
                    id="analytics-cookies"
                    type="checkbox" 
                    checked={consent.analytics}
                    readOnly
                  />
                  <Slider />
                </Switch>
              </ConsentOption>
              
              <ConsentOption>
                <OptionLabel htmlFor="marketing-cookies" onClick={() => handleOptionChange('marketing')}>
                  {t('cookieConsent.marketing', 'Marketing Cookies')}
                  <OptionDescription>
                    {t('cookieConsent.marketingDesc', 'Used to track visitors across websites to display relevant advertisements.')}
                  </OptionDescription>
                </OptionLabel>
                <Switch onClick={() => handleOptionChange('marketing')}>
                  <SwitchInput 
                    id="marketing-cookies"
                    type="checkbox" 
                    checked={consent.marketing}
                    readOnly
                  />
                  <Slider />
                </Switch>
              </ConsentOption>
            </SettingsContainer>
            
            <ButtonGroup>
              <DeclineButton onClick={() => setShowSettings(false)}>
                {t('cookieConsent.cancel', 'Cancel')}
              </DeclineButton>
              <AcceptButton onClick={handleSaveSettings}>
                {t('cookieConsent.saveSettings', 'Save Settings')}
              </AcceptButton>
            </ButtonGroup>
          </>
        ) : (
          <ButtonGroup>
            <DeclineButton onClick={handleDeclineAll}>
              {t('cookieConsent.decline', 'Decline All')}
            </DeclineButton>
            <SettingsButton onClick={() => setShowSettings(true)}>
              {t('cookieConsent.settings', 'Customize')}
            </SettingsButton>
            <AcceptButton onClick={handleAcceptAll}>
              {t('cookieConsent.accept', 'Accept All')}
            </AcceptButton>
          </ButtonGroup>
        )}
      </ConsentContainer>
    </>
  );
};

export default CookieConsent; 